import React, { useMemo } from 'react'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { LucideIcon } from 'utils/lucideIcon'
import { TooltipWrapper } from 'uiComponents/tooltip.new'
import { computeDiff, DiffGroup, groupByLabel } from './diff'
import { AuditLogEntry, OBJECT_TYPE_LABELS, SOURCE_LABELS, STAFF_MASK } from './schema'

const actorLabel = (actor: string | null) => (actor === STAFF_MASK ? 'Convious staff' : actor ?? 'Unknown user')

const ACTION_LABELS: Record<AuditLogEntry['action'], string> = {
    created: 'Created',
    updated: 'Changed',
    deleted: 'Deleted',
}

const MAX_VISIBLE_CHANGES = 4

const groupText = ({ label, entries }: DiffGroup, action: AuditLogEntry['action']) =>
    [
        label,
        ...entries.map((e) => {
            if (action !== 'updated') return e.after ?? e.before
            if (e.kind === 'changed') return `${e.before} → ${e.after}`
            if (e.kind === 'removed') return `−${e.before}`
            return `added ${e.after}`
        }),
    ].join(' ')

const ChangeLine = ({ group, action }: { group: DiffGroup; action: AuditLogEntry['action'] }) => (
    <span className="audit-change" title={groupText(group, action)}>
        <span>{group.label}</span>
        {group.entries.map((e, i) => (
            <React.Fragment key={i}>
                {action === 'updated' && e.kind === 'added' && <span className="audit-change-added">added</span>}
                {action === 'updated' && e.before !== undefined && <s className="audit-change-before">{e.before}</s>}
                {(action !== 'updated' || e.after !== undefined) && (
                    <span className="audit-change-after">{e.after ?? e.before}</span>
                )}
            </React.Fragment>
        ))}
    </span>
)

const sourcePhrase = (source: AuditLogEntry['source']) =>
    `from ${source === 'api' ? 'a connected system (API)' : SOURCE_LABELS[source] ?? source}`

interface WhatCellProps {
    entry: AuditLogEntry
    objectPath: string | null
    onSelectObject: (objectId: string) => void
}

export const WhatCell = ({ entry, objectPath, onSelectObject }: WhatCellProps) => {
    const typeLabel = OBJECT_TYPE_LABELS[entry.objectType] ?? entry.objectType
    const objectName = entry.objectName || 'Unnamed'
    const title = `${ACTION_LABELS[entry.action]} ${typeLabel.toLowerCase()}`
    const filterTitle = `Show only changes to this ${typeLabel.toLowerCase()}`

    return (
        <div className="audit-what">
            <div className="audit-what-title" title={title}>
                <span className="audit-action">{ACTION_LABELS[entry.action]}</span>{' '}
                <span className="audit-type">{typeLabel.toLowerCase()}</span>
            </div>
            <div className="audit-what-object">
                {objectPath ? (
                    <Link to={objectPath} className="audit-object-link" title={`Open this ${typeLabel.toLowerCase()}`}>
                        {objectName}
                    </Link>
                ) : (
                    <span className="audit-object-name" title={objectName}>
                        {objectName}
                    </span>
                )}
                <button
                    type="button"
                    className="audit-object-filter"
                    title={filterTitle}
                    aria-label={filterTitle}
                    onClick={() => onSelectObject(entry.objectId)}
                >
                    <LucideIcon iconName="FunnelPlus" size={14} />
                </button>
            </div>
        </div>
    )
}

export const ChangesCell = ({ entry }: { entry: AuditLogEntry }) => {
    const diff = useMemo(() => computeDiff(entry.stateBefore, entry.stateAfter), [entry])
    const groups = groupByLabel(diff)
    const visible = groups.slice(0, MAX_VISIBLE_CHANGES)
    const rest = groups.length - visible.length

    return (
        <div className="audit-changes">
            {groups.length === 0 && <span className="audit-change">No visible field changed</span>}
            {visible.map((group) => (
                <ChangeLine key={group.label} group={group} action={entry.action} />
            ))}
            {rest > 0 && (
                <TooltipWrapper
                    className="audit-more"
                    text={groups.map((g) => groupText(g, entry.action)).join('\n')}
                    position="bottom"
                >
                    <span className="audit-more-target">and {rest} more</span>
                </TooltipWrapper>
            )}
        </div>
    )
}

export const WhoCell = ({ entry }: { entry: AuditLogEntry }) => (
    <div className="audit-who">
        <div className="audit-actor-line">
            <span className="audit-actor" title={actorLabel(entry.actor)}>
                {actorLabel(entry.actor)}
            </span>
            {entry.impersonatedBy && (
                <TooltipWrapper
                    className="audit-impersonation"
                    text={`${actorLabel(entry.impersonatedBy)} made this change for this user`}
                    position="bottom"
                    arrowPosition="left"
                >
                    <LucideIcon iconName="VenetianMask" size={16} />
                </TooltipWrapper>
            )}
        </div>
        <div className="audit-source" title={sourcePhrase(entry.source)}>
            {sourcePhrase(entry.source)}
        </div>
    </div>
)

export const WhenCell = ({ createdAt }: { createdAt: string }) => (
    <div className="audit-when" title={createdAt}>
        {format(createdAt, 'd MMM yyyy, HH:mm')}
    </div>
)
